import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tag, X } from "lucide-react";
import API from "@/lib/api";
import { useBottomRightStack } from "@/components/BottomRightStack";

/** Floating "Compare plans" pill — appears on public pages after the visitor
 *  scrolls past the hero. Hidden on /pricing itself (already there) and after
 *  the user dismisses it for the session. Designed for thumb-reach on mobile,
 *  sits above the bottom navigation safe-area on iOS. */
export default function StickyComparePlansFab() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [fromPrice, setFromPrice] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("glpflo-fab-dismissed") === "1") {
      setDismissed(true);
      return;
    }
    const onScroll = () => {
      // Only show after they've engaged with the page (~one viewport down).
      setVisible(window.scrollY > Math.min(600, window.innerHeight * 0.6));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Register in the bottom-right stack so the chat widget lifts above
  // us when we're occupying the corner. We don't apply the returned
  // bottom px ourselves — we stay anchored to `bottom-4 / sm:bottom-6`
  // because we're the lowest-priority corner element other than the
  // cookie banner (which doesn't horizontally overlap us on desktop).
  const fabActive = !dismissed && pathname !== "/pricing" && visible;
  useBottomRightStack({
    id: "compare-plans-fab",
    priority: 20,
    visible: fabActive,
    height: 48, // pill is ~48px tall (py-3 = 12+12 + ~24 content)
  });

  // Pull the lowest active Royal Tier price so the badge always tracks
  // whatever the super-admin has currently configured.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/pricing");
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        // Scan every dose_group too so the pill reflects the real minimum
        // (patients on low doses often see the cheapest Royal price).
        const mins = [];
        data.forEach((d) => {
          if (Number(d.royal_tier_price)) mins.push(Number(d.royal_tier_price));
          (d.dose_groups || []).forEach((g) => {
            if (Number(g.royal_tier_price)) mins.push(Number(g.royal_tier_price));
          });
        });
        const min = mins.reduce((a, b) => Math.min(a, b), Infinity);
        if (Number.isFinite(min)) setFromPrice(min);
      } catch {
        // Silent — the fab still works without a live price hint.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Never render on the pricing page itself or in dismissed state.
  if (dismissed || pathname === "/pricing") return null;

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem("glpflo-fab-dismissed", "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className={`fixed z-40 right-4 bottom-4 sm:right-6 sm:bottom-6 transition-all duration-300 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
      }`}
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="sticky-compare-plans-fab"
    >
      <Link
        to="/pricing"
        className="group inline-flex items-center gap-2.5 pl-5 pr-3 py-3 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-2xl shadow-purple-500/40 ring-1 ring-white/20 hover:scale-[1.03] hover:shadow-purple-500/60 active:scale-[0.99] transition-all"
        data-testid="sticky-compare-plans-link"
      >
        <Tag className="w-4 h-4 text-amber-300 group-hover:rotate-[-8deg] transition-transform" />
        <span className="text-sm font-semibold whitespace-nowrap">Compare plans</span>
        <span className="hidden sm:inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-white/15">
          {fromPrice ? `From $${fromPrice}/mo` : "Transparent pricing"}
        </span>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="ml-1 w-6 h-6 inline-flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition-colors"
          data-testid="sticky-compare-plans-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </Link>
    </div>
  );
}
